import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './contingencyTable.Renderer';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { PLACEHOLDER_OTHER } from '../../helpers/categoricalTools';

export function ContingencyTable({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const { definition, dataTable, areaPoints, colCatsX, colCatsY, contingencyTable, title } = createDefinition(
    chartTypeManifest,
    dataContext,
    dataBinding,
    config,
  );

  const categoriesXData = contingencyTable.categoriesX.categories;
  const categoriesYData = contingencyTable.categoriesY.categories;
  const cells = contingencyTable.cells;
  const catCountX = categoriesXData.length;
  const catCountY = categoriesYData.length;

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id == 'contingency') {
      const catIdxX = Math.floor(logicPositX * categoriesXData.length);
      const catIdxY = Math.floor(logicPositY * categoriesYData.length);
      if (catIdxX >= 0 && catIdxX < categoriesXData.length && catIdxY >= 0 && catIdxY < categoriesYData.length) {
        return {
          dataTableId: dataBinding.dataTableId,
          itemId: {
            catIdxX,
            catIdxY,
          },
          custom: {
            catXColId: colCatsX.id,
            catYColId: colCatsY.id,
            catXVal: categoriesXData[catIdxX].value,
            catYVal: categoriesYData[catIdxY].value,
            catIdxX,
            catIdxY,
          },
        };
      }
    }
    return null;
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId) return null;
    if (hoverInfo.custom && hoverInfo.custom.catXColId == colCatsX.id && hoverInfo.custom.catYColId == colCatsY.id) {
      const catXVal = hoverInfo.custom.catXVal;
      const catYVal = hoverInfo.custom.catYVal;
      const catIdxX = hoverInfo.custom.catIdxX;
      const catIdxY = hoverInfo.custom.catIdxY;
      const labels: string[] = [];
      if (context.isActiveChart) {
        labels.push(convertColumnDataToDisplayString(colCatsX, catXVal));
        labels.push(convertColumnDataToDisplayString(colCatsY, catYVal));
        labels.push(`${cells[catIdxX][catIdxY].countSelected} / ${cells[catIdxX][catIdxY].count}`);
      }
      return {
        area: areaPoints,
        logicPosX: (hoverInfo.custom.catIdxX + 0.5) / categoriesXData.length,
        logicPosY: (hoverInfo.custom.catIdxY + 0.5) / categoriesYData.length,
        labels,
      };
    }
    return null;
  }

  function onItemClicked(itemId: any) {
    const clickedCell = cells[itemId.catIdxX][itemId.catIdxY];
    if (clickedCell.valX == PLACEHOLDER_OTHER || clickedCell.valY == PLACEHOLDER_OTHER) return;
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.DUALCATEGORICAL,
      dataTableId: dataTable.id,
      colId1: colCatsX.id,
      colId2: colCatsY.id,
      selectedCategoryId1: clickedCell.valX,
      selectedCategoryId2: clickedCell.valY,
    });
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        widgetContext={widgetContext}
        onItemClicked={onItemClicked}
        maxZoomXAbsolute={catCountX * 20}
        maxZoomYAbsolute={catCountY * 20}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
